import { c as _c } from "react/compiler-runtime";
import { FiDroplet } from "react-icons/fi";
import { Tooltip } from "@/components/ui";
import { formatDilutionLabel, formatDilutionStrength } from "@/lib/dilution";
import { cx } from "@/lib/tailwind-utils";
import type { DoseComponent } from "@/types/medication";
type DrawnDilution = Pick<DoseComponent, "diluentVolume" | "vialStrength">;

/** One distinct reconstitution per entry, in the order the components list them. */
function distinctDilutions(components: DrawnDilution[]) {
  const seen = new Set<number>();
  return components.filter(component => {
    if (!component.diluentVolume || seen.has(component.diluentVolume)) {
      return false;
    }
    seen.add(component.diluentVolume);
    return true;
  }) as (DrawnDilution & {
    diluentVolume: number;
  })[];
}
function describe(dilution: DrawnDilution & {
  diluentVolume: number;
}) {
  const strength = formatDilutionStrength(dilution);
  return [`${formatDilutionLabel(dilution.diluentVolume)} diluent`, dilution.vialStrength ? `${dilution.vialStrength} per vial` : null, strength].filter(Boolean).join(" · ");
}

/**
 * The reconstitution a dose was drawn at, as quiet metadata beside the
 * medication: it describes the dose, so it isn't styled as a status.
 */
export const DoseDilutionLabel = t0 => {
  const $ = _c(16);
  const {
    components,
    className
  } = t0;
  let T0;
  let label;
  let t1;
  let t2;
  if ($[0] !== components) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const drawn = distinctDilutions(components);
      if (!drawn.length) {
        t2 = null;
        break bb0;
      }
      label = drawn.map(_temp).join(", ");
      T0 = Tooltip;
      t1 = drawn.map(_temp2);
    }
    $[0] = components;
    $[1] = T0;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    T0 = $[1];
    label = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[5] !== className) {
    t3 = cx("inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-caption text-foreground-muted tabular-nums", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `Dilution ${label}`;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FiDroplet className="size-3" aria-hidden="true" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label || $[9] !== t3 || $[10] !== t4) {
    t6 = <span className={t3} aria-label={t4}>{t5}{label}</span>;
    $[8] = label;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== T0 || $[13] !== t1 || $[14] !== t6) {
    t7 = <T0 content={t1} triggerAsChild={true}>{t6}</T0>;
    $[12] = T0;
    $[13] = t1;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function _temp(dilution) {
  return formatDilutionLabel(dilution.diluentVolume);
}
function _temp2(dilution_0) {
  return <div key={dilution_0.diluentVolume}>{describe(dilution_0)}</div>;
}